
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

export const useUrlParams = () => {
  // IMMEDIATELY check URL and set recovery state BEFORE any React rendering
  const getInitialRecoveryState = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const type = urlParams.get('type');
    const token = urlParams.get('token');
    const access_token = urlParams.get('access_token');
    const refresh_token = urlParams.get('refresh_token');
    const token_hash = urlParams.get('token_hash');
    
    console.log("=== INITIAL RECOVERY CHECK ===");
    console.log("URL:", window.location.href);
    console.log("Type:", type);
    console.log("Has token:", !!token);
    console.log("Has token_hash:", !!token_hash);
    console.log("Has access/refresh:", !!access_token, !!refresh_token);
    
    // Enhanced recovery detection - ANY of these patterns indicates recovery
    const isRecovery = type === 'recovery' || 
                      type === 'magiclink' ||
                      (access_token && refresh_token && type === 'recovery') ||
                      (token && type === 'recovery') ||
                      (token_hash && type === 'recovery') ||
                      // Also check for URL fragment patterns (some Supabase versions use fragments)
                      window.location.hash.includes('type=recovery') ||
                      window.location.hash.includes('access_token') && window.location.hash.includes('recovery');
    
    console.log("Recovery detected:", isRecovery);
    
    // IMMEDIATELY set session storage AND a global flag
    if (isRecovery) {
      console.log("IMMEDIATE: Setting recovery state");
      sessionStorage.setItem('isPasswordRecovery', 'true');
      // Set a global flag that can be checked synchronously
      (window as any).__passwordRecoveryActive = true;
    }
    
    return isRecovery;
  };

  // Also check if we're already in recovery mode from storage
  const getInitialFromStorage = () => {
    const isRecoveryFromStorage = sessionStorage.getItem('isPasswordRecovery') === 'true';
    if (isRecoveryFromStorage) {
      (window as any).__passwordRecoveryActive = true;
    }
    return isRecoveryFromStorage;
  };

  const initialRecoveryState = getInitialRecoveryState() || getInitialFromStorage();
  
  const [isRecoveryFlow, setIsRecoveryFlow] = useState<boolean>(initialRecoveryState);
  const [isPasswordReset, setIsPasswordReset] = useState<boolean>(initialRecoveryState);
  const [message, setMessage] = useState<string>(initialRecoveryState ? "Please enter your new password below." : "");
  const [error, setError] = useState<string>("");
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const token = searchParams.get('token');
    const accessToken = searchParams.get('access_token');
    const refreshToken = searchParams.get('refresh_token');
    const type = searchParams.get('type');
    const tokenHash = searchParams.get('token_hash');
    const error_code = searchParams.get('error_code');
    const error_description = searchParams.get('error_description');
    
    console.log("=== URL PARAMS EFFECT ===");
    console.log("Current URL:", window.location.href);
    console.log("Parameters:", {
      type,
      token: !!token,
      tokenHash: !!tokenHash,
      accessToken: !!accessToken,
      refreshToken: !!refreshToken,
      error_code,
      error_description
    });

    // Handle errors first
    if (error_code || error_description) {
      console.log("Error in URL params:", { error_code, error_description });
      setError(error_description || "An error occurred during authentication");
      setIsPasswordReset(false);
      setIsRecoveryFlow(false);
      sessionStorage.removeItem('isPasswordRecovery');
      (window as any).__passwordRecoveryActive = false;
      return;
    }

    // Enhanced recovery flow detection with fragment support
    const urlFragment = window.location.hash;
    const isRecovery = type === 'recovery' || 
                      type === 'magiclink' ||
                      (accessToken && refreshToken) ||
                      (token && type === 'recovery') ||
                      (tokenHash && type === 'recovery') ||
                      urlFragment.includes('type=recovery') ||
                      (urlFragment.includes('access_token') && urlFragment.includes('recovery'));
    
    if (isRecovery) {
      console.log("RECOVERY FLOW DETECTED");
      setIsRecoveryFlow(true);
      setIsPasswordReset(true);
      setMessage("Please enter your new password below.");
      setError("");
      
      // Ensure recovery state is preserved
      sessionStorage.setItem('isPasswordRecovery', 'true');
      (window as any).__passwordRecoveryActive = true;
      
      // Handle different recovery token formats
      if (accessToken && refreshToken) {
        console.log("Setting session from access/refresh tokens");
        supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        }).then(({ error }) => {
          if (error) {
            console.error("Session setting error:", error);
            setError("Invalid or expired reset link. Please request a new password reset.");
            setIsPasswordReset(false);
            setIsRecoveryFlow(false);
            sessionStorage.removeItem('isPasswordRecovery');
            (window as any).__passwordRecoveryActive = false;
          }
        });
        return;
      }

      if (tokenHash) {
        console.log("Verifying token hash");
        supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type: 'recovery'
        }).then(({ error }) => {
          if (error) {
            console.error("Token hash verification error:", error);
            setError("Invalid or expired reset link. Please request a new password reset.");
            setIsPasswordReset(false);
            setIsRecoveryFlow(false);
            sessionStorage.removeItem('isPasswordRecovery');
            (window as any).__passwordRecoveryActive = false;
          }
        });
        return;
      }

      if (token) {
        console.log("Verifying token");
        supabase.auth.verifyOtp({
          token_hash: token,
          type: 'recovery'
        }).then(({ error }) => {
          if (error) {
            console.error("Token verification error:", error);
            setError("Invalid or expired reset link. Please request a new password reset.");
            setIsPasswordReset(false);
            setIsRecoveryFlow(false);
            sessionStorage.removeItem('isPasswordRecovery');
            (window as any).__passwordRecoveryActive = false;
          }
        });
        return;
      }
    }

    // Handle email confirmation
    if (type === 'signup' && accessToken && refreshToken) {
      setMessage("Email confirmed successfully! You are now signed in.");
      setError("");
    }

    // Clear recovery states if not a recovery flow
    if (!isRecovery) {
      console.log("Not a recovery flow, clearing states");
      setIsRecoveryFlow(false);
      setIsPasswordReset(false);
      sessionStorage.removeItem('isPasswordRecovery');
      (window as any).__passwordRecoveryActive = false;
    }
  }, [searchParams]);

  return {
    isRecoveryFlow,
    isPasswordReset,
    message,
    error,
    setIsRecoveryFlow: (value: boolean) => {
      setIsRecoveryFlow(value);
      if (!value) {
        sessionStorage.removeItem('isPasswordRecovery');
        (window as any).__passwordRecoveryActive = false;
      }
    },
    setIsPasswordReset: (value: boolean) => {
      setIsPasswordReset(value);
      if (!value) {
        sessionStorage.removeItem('isPasswordRecovery');
        (window as any).__passwordRecoveryActive = false;
      }
    },
    setMessage,
    setError
  };
};
